import { UpdateNotice } from '@/pwa/UpdateNotice';
import { HintButton as Button } from '@/components/base/HintButton';
import { DiscoveryHeader } from '@/discovery/DiscoveryHeader';
import { readAttemptProjectReferences } from '@/challenges/attemptProjectReferences';
import { useEffect, useMemo, useState } from 'react';
import { ProjectShareDialog } from '@/projectSharing/ProjectShareDialog';
import { ProjectShareHistory } from '@/projectSharing/ProjectShareHistory';
import { useOptionalProjectSharing } from '@/projectSharing/services';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import {
  InputGroup,
  Alert,
  Classes,
  Callout,
  Dialog,
  DialogBody,
  DialogFooter,
} from '@blueprintjs/core';

import { DiscoveryLayout, discoveryStyles as styles } from '@/discovery/DiscoveryLayout';
import { useAppModel } from '@/models/AppModel';
import { downloadProjectFile, pickCurrentProjectFile } from '@/utils/projectFileIO';
import { StartPageViewModel } from './StartPageViewModel';
import { SelectField } from '@/components/base/SelectField';
import { projectSortPreference, type ProjectSort } from './projectSortPreference';
import { ProjectListHeader, ProjectListRows } from './ProjectListRows';

export const StartPage = observer(function StartPage() {
  const appModel = useAppModel();
  const sharingEnabled = useOptionalProjectSharing()?.enabled ?? false;
  const navigate = useNavigate();
  const [shareTarget, setShareTarget] = useState<string>();
  const [shareHistoryOpen, setShareHistoryOpen] = useState(false);
  const vm = useMemo(
    () =>
      new StartPageViewModel(
        appModel,
        {
          pick: pickCurrentProjectFile,
          download: downloadProjectFile,
        },
        {
          readReferences: readAttemptProjectReferences,
          readIdentity: (name, signal) => appModel.readSavedProjectIdentity(name, signal),
        },
        projectSortPreference,
      ),
    [appModel],
  );

  useEffect(() => {
    vm.mount();
    // Another tab may save, import or remove projects while Home stays mounted.
    const refreshOnReturn = () => {
      if (document.visibilityState !== 'hidden') void vm.refreshProjects();
    };
    const stopHistory = appModel.watchSavedProjectOpenHistory?.(refreshOnReturn);
    window.addEventListener('focus', refreshOnReturn);
    document.addEventListener('visibilitychange', refreshOnReturn);
    return () => {
      stopHistory?.();
      window.removeEventListener('focus', refreshOnReturn);
      document.removeEventListener('visibilitychange', refreshOnReturn);
      vm.unmount();
    };
  }, [vm]);

  const handleCreate = async () => {
    const name = vm.newProjectName.trim() || 'Project';
    navigate(`/editor?project=${encodeURIComponent(name)}`);
  };

  const onlyAttempts =
    !vm.search.trim() && vm.savedProjects.length > 0 && vm.visibleProjects.length === 0;
  return (
    <DiscoveryLayout>
      <UpdateNotice />
      <DiscoveryHeader
        title="Projects"
        actions={
          <>
            <Button intent="primary" icon="plus" onClick={() => vm.setCreateOpen(true)}>
              New project
            </Button>
            {sharingEnabled && (
              <Button icon="link" onClick={() => setShareHistoryOpen(true)}>
                Shared links
              </Button>
            )}
            <Button icon="import" onClick={() => void vm.projectAction(vm.importProject)}>
              Import
            </Button>
          </>
        }
      />
      <div className={styles.toolbar}>
        <div className={styles.search}>
          <label htmlFor="project-search">Search projects</label>
          <InputGroup
            id="project-search"
            leftIcon="search"
            placeholder="Search projects"
            value={vm.search}
            onChange={(e) => vm.setSearch(e.target.value)}
            fill
          />
        </div>
        <div className={styles.filter}>
          <span>Sort</span>
          <SelectField<ProjectSort>
            label="Sort projects"
            value={vm.sort}
            onChange={vm.setSort}
            options={[
              { value: 'opened-desc', label: 'Most recently opened' },
              { value: 'asc', label: 'A–Z' },
              { value: 'desc', label: 'Z–A' },
            ]}
          />
        </div>
      </div>
      <section aria-label="Saved projects">
        <ProjectListHeader
          status={vm.projectListState.status}
          actionFailed={!!vm.actionError}
        />
        {vm.projectListState.status === 'ready' && !vm.displayProjects.length && (
          <div className={styles.empty} role="status">
            <h2>
              {onlyAttempts
                ? 'No ordinary projects'
                : vm.savedProjects.length
                  ? 'No matching projects'
                  : 'Your projects start here'}
            </h2>
            <p>
              {onlyAttempts
                ? 'Your challenge attempts are available in Challenges.'
                : vm.savedProjects.length
                  ? 'Try another search.'
                  : 'Create a project or import a DigiTiles file. Your work is stored on this device.'}
            </p>
          </div>
        )}
        <ProjectListRows
          names={vm.displayProjects}
          lastOpened={vm.lastOpened}
          onRename={vm.startRename}
          onShare={sharingEnabled ? setShareTarget : undefined}
          onExport={(name) => void vm.projectAction(() => vm.exportProject(name))}
          onDuplicate={(name) => void vm.projectAction(() => vm.copyProject(name))}
          onDelete={vm.setDeleteTarget}
        />

        {vm.projectListState.status === 'error' && (
          <Callout intent="danger" role="alert">
            <p>Unable to list saved projects.</p>
            <p>{vm.projectListState.message}</p>
            <Button icon="refresh" onClick={() => void vm.refreshProjects()}>
              Retry
            </Button>
          </Callout>
        )}
      </section>
      {vm.actionError && (
        <Callout intent="danger" role="alert">
          {vm.actionError.message}
        </Callout>
      )}

      <footer className={styles.footer}>
        <span>Stored on this device</span>
        <Button
          variant="minimal"
          icon="add-to-artifact"
          onClick={() => navigate('/extensions')}
        >
          Manage extensions
        </Button>
      </footer>
      {shareTarget && (
        <ProjectShareDialog
          key={shareTarget}
          name={shareTarget}
          onManageShares={() => {
            setShareTarget(undefined);
            setShareHistoryOpen(true);
          }}
          onClose={() => setShareTarget(undefined)}
        />
      )}
      {shareHistoryOpen && (
        <ProjectShareHistory onClose={() => setShareHistoryOpen(false)} />
      )}
      <Dialog
        isOpen={vm.createOpen}
        onClose={() => vm.setCreateOpen(false)}
        title="New project"
        className={Classes.DARK}
      >
        <DialogBody>
          <label htmlFor="new-project-name">Project name</label>
          <InputGroup
            id="new-project-name"
            placeholder="Project"
            value={vm.newProjectName}
            autoFocus
            onChange={(e) => vm.setNewProjectName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleCreate();
            }}
          />
        </DialogBody>
        <DialogFooter
          actions={
            <>
              <Button onClick={() => vm.setCreateOpen(false)}>Cancel</Button>
              <Button intent="primary" onClick={handleCreate}>
                Create
              </Button>
            </>
          }
        />
      </Dialog>

      <Dialog
        isOpen={vm.editingProject !== null}
        onClose={vm.cancelRename}
        title="Rename project"
        className={Classes.DARK}
      >
        <DialogBody>
          <label htmlFor="rename-project-name">Project name</label>
          <InputGroup
            id="rename-project-name"
            value={vm.editName}
            autoFocus
            onChange={(e) => vm.setEditName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && vm.editingProject)
                void vm.projectAction(() =>
                  vm.renameProject(vm.editingProject!, vm.editName),
                );
            }}
          />
        </DialogBody>
        <DialogFooter
          actions={
            <>
              <Button onClick={vm.cancelRename}>Cancel</Button>
              <Button
                intent="primary"
                onClick={() =>
                  vm.editingProject &&
                  void vm.projectAction(() =>
                    vm.renameProject(vm.editingProject!, vm.editName),
                  )
                }
              >
                Rename
              </Button>
            </>
          }
        />
      </Dialog>

      <Alert
        isOpen={vm.deleteTarget !== null}
        onConfirm={() => vm.deleteTarget && vm.deleteProject(vm.deleteTarget)}
        onCancel={() => vm.setDeleteTarget(null)}
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
        intent="danger"
        icon="trash"
      >
        <p>
          Are you sure you want to delete <strong>{vm.deleteTarget}</strong>? This cannot
          be undone.
        </p>
      </Alert>
    </DiscoveryLayout>
  );
});
